import React, { useEffect, useMemo, useState } from 'react';
import Icon from "components/ui/AppIcon.jsx";
import { getComplianceSeries } from '../../../utils/api';

function fmtPct(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Math.round(v)}%`;
}
function fmtQuality(v) {
  if (v == null || Number.isNaN(v)) return '—';
  return (Math.round(v * 10) / 10).toFixed(1);
}

// Clasificación visual por umbrales
function statusFor(metricId, value) {
  if (value == null) return 'warning';
  if (metricId === 'quality') {
    if (value >= 8.5) return 'success';
    if (value >= 7.0) return 'warning';
    return 'error';
  }
  // accuracy / compliance (%)
  if (value >= 95) return 'success';
  if (value >= 85) return 'warning';
  return 'error';
}

function trendFor(delta) {
  return delta >= 0 ? 'up' : 'down';
}

function getStatusColor(status) {
  switch (status) {
    case 'success': return 'text-success bg-success/10 border-success/20';
    case 'warning': return 'text-warning bg-warning/10 border-warning/20';
    case 'error':   return 'text-error bg-error/10 border-error/20';
    default:        return 'text-muted-foreground bg-muted border-border';
  }
}

function getTrendIcon(trend) {
  return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
}
function getTrendColor(trend, status) {
  if (status === 'error' && trend === 'up') return 'text-error';
  return trend === 'up' ? 'text-success' : 'text-error';
}

const ComplianceMetrics = () => {
  const [state, setState] = useState({ loading: true, error: null, series: [] });

  useEffect(() => {
    (async () => {
      try {
        setState(s => ({ ...s, loading: true, error: null }));
        // 90 días → bucket semanal en getComplianceSeries
        const rows = await getComplianceSeries({ period: '90d' });
        setState({ loading: false, error: null, series: rows || [] });
      } catch (e) {
        console.error('[ComplianceMetrics] load error', e);
        setState({ loading: false, error: e, series: [] });
      }
    })();
  }, []);

  // Promedios de últimas 4 semanas vs 4 previas (fallback a lo disponible)
  const { curAcc, prevAcc, curCom, prevCom, curQual, prevQual } = useMemo(() => {
    const s = state.series || [];
    const n = s.length;
    const take = 4;
    const cur = n ? s.slice(Math.max(0, n - take)) : [];
    const prev = n > take ? s.slice(Math.max(0, n - take * 2), n - take) : [];

    const avg = (arr, key) =>
      arr.length ? arr.reduce((sum, r) => sum + (Number(r[key]) || 0), 0) / arr.length : null;

    return {
      curAcc: avg(cur, 'accuracy'),
      prevAcc: avg(prev, 'accuracy'),
      curCom: avg(cur, 'compliance'),
      prevCom: avg(prev, 'compliance'),
      curQual: avg(cur, 'quality'),
      prevQual: avg(prev, 'quality'),
    };
  }, [state.series]);

  const deltaAcc = (curAcc != null && prevAcc != null) ? (curAcc - prevAcc) : 0;
  const deltaCom = (curCom != null && prevCom != null) ? (curCom - prevCom) : 0;
  const deltaQual = (curQual != null && prevQual != null) ? (curQual - prevQual) : 0;

  const metrics = [
    {
      id: 'accuracy',
      title: 'Precisión de Calibración',
      value: curAcc != null ? fmtPct(curAcc) : '—',
      change: `${deltaAcc >= 0 ? '+' : ''}${Math.round(deltaAcc)}%`,
      trend: trendFor(deltaAcc),
      status: statusFor('accuracy', curAcc),
      icon: 'Target',
      description: 'Promedio últimas 4 semanas',
    },
    {
      id: 'compliance',
      title: 'Cumplimiento Certificación',
      value: curCom != null ? fmtPct(curCom) : '—',
      change: `${deltaCom >= 0 ? '+' : ''}${Math.round(deltaCom)}%`,
      trend: trendFor(deltaCom),
      status: statusFor('compliance', curCom),
      icon: 'Shield',
      description: 'Calibraciones a tiempo',
    },
    {
      id: 'quality',
      title: 'Puntuación de Calidad',
      value: curQual != null ? fmtQuality(curQual) : '—',
      change: `${deltaQual >= 0 ? '+' : ''}${(Math.round(deltaQual * 10) / 10).toFixed(1)}`,
      trend: trendFor(deltaQual),
      status: statusFor('quality', curQual),
      icon: 'Award',
      description: 'Escala 0–10 (60% precisión, 40% cumplimiento)',
    },
    // Para activar esta tarjeta cuando exista la fuente real:
    // {
    //   id: 'audits',
    //   title: 'Auditorías Pendientes',
    //   value: pendingAuditsCount?.toString() ?? '—',
    //   change: signedDelta(pendingAuditsDelta),
    //   trend: trendFor(pendingAuditsDelta),
    //   status: statusForAudits(pendingAuditsCount),
    //   icon: 'FileSearch',
    //   description: 'Requieren atención',
    // },
  ];

  if (state.loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-card border rounded-lg p-6 animate-pulse">
            <div className="flex items-start justify-between mb-4">
              <div className="p-2 rounded-lg bg-muted w-10 h-10" />
              <div className="h-4 w-16 bg-muted rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-4 w-32 bg-muted rounded" />
              <div className="h-7 w-20 bg-muted rounded" />
              <div className="h-3 w-40 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="p-6 bg-card border rounded-lg text-sm text-destructive">
        Error al cargar métricas: {state.error.message}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {metrics.map((metric) => (
        <div
          key={metric.id}
          className={`bg-card border rounded-lg p-6 transition-all duration-200 hover:shadow-md ${getStatusColor(metric.status)}`}
        >
          <div className="flex items-start justify-between mb-4">
            <div className={`p-2 rounded-lg ${getStatusColor(metric.status)}`}>
              <Icon name={metric.icon} size={20} />
            </div>
            <div className={`flex items-center space-x-1 text-sm ${getTrendColor(metric.trend, metric.status)}`}>
              <Icon name={getTrendIcon(metric.trend)} size={14} />
              <span className="font-medium">{metric.change}</span>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              {metric.title}
            </h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-2xl font-bold text-foreground">
                {metric.value}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {metric.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ComplianceMetrics;
